import { createServerFn } from '@tanstack/react-start';
import { getCookie, setCookie } from '@tanstack/react-start/server';

import { isResearcherV3Enabled } from '../feature-flags-server';
import { captureServerException } from '../posthog-server-errors';
import { SESSION_COOKIE_NAME } from './constants';
import {
  buildClearSessionCookie,
  destroySession,
  renewSessionTTL,
} from './create-session';
import { dashboardFromSession, type Session } from './dashboard-from-session';
import { fetchUser } from './fetch-user';
import {
  getSessionFromRedis,
  getPassportUserFromSession,
  pickPublicSessionState,
} from './get-session-from-redis';
import { parseSessionCookie } from './parse-cookie';
import { updateSessionState } from './update-session-state';

/**
 * Server function to get the current session.
 * Reads the respondent.session.sid cookie, validates against Redis,
 * and fetches fresh user data from core-api.
 *
 * Use this in beforeLoad or loaders to check authentication server-side.
 *
 * @returns The session with user data, or null if not authenticated
 */
export const getServerSession = createServerFn().handler(
  async (): Promise<Session | null> => {
    const sessionCookie = getCookie(SESSION_COOKIE_NAME);

    // 1. Parse session cookie
    const sessionId = sessionCookie
      ? parseSessionCookie(sessionCookie)
      : undefined;
    if (!sessionId) {
      return null;
    }

    // 2. Fetch session from Redis
    const sessionData = await getSessionFromRedis(sessionId);
    if (!sessionData) {
      return null;
    }

    // 3. Extract passport user from session
    const passportUser = getPassportUserFromSession(sessionData);
    if (!passportUser) {
      return null;
    }

    // 4. Fetch fresh user data from core-api
    // TODO: We might not even need this, or we can maybe cache it?
    const user = await fetchUser(passportUser.id);
    if (!user) {
      return null;
    }

    // 5. Renew session TTL and refresh cookie (sliding window expiry).
    // Deliberately after the user is validated: renewing first kept sessions
    // of deactivated users alive indefinitely (every page view reset the TTL
    // even though core-api rejected the user), so they could never age out.
    renewSessionTTL(sessionId, setCookie);

    let v3Enabled = sessionData.v3Enabled;
    // Only re-evaluate the org-scoped researcher-V3 flag once a team has
    // actually been selected. Before that there is no org to scope it to:
    // `resolveActiveDashboard` sends the user to `/researchers/select-team`
    // (or auto-persists their only team), and `persistActiveDashboard`
    // evaluates the flag as part of writing the selection.
    const activeOrganization = dashboardFromSession({
      sessionState: passportUser.sessionState,
    }).organization;
    if (user.roles.includes('researcher') && activeOrganization) {
      v3Enabled = await isResearcherV3Enabled(user.id, activeOrganization);
      try {
        await updateSessionState(sessionId, {}, { v3Enabled });
      } catch (error) {
        captureServerException(error, user.id, {
          path: '/session/getServerSession',
          errorContext: 'updateSessionState failed',
        });
      }
    }

    return {
      user,
      // Allowlisted copy: this response is serialized to the browser, and the
      // raw Redis state can carry more than the SessionState type declares.
      sessionState: pickPublicSessionState(passportUser.sessionState),
      v3Enabled,
    };
  },
);

/**
 * Server function to sign out.
 * Destroys the session in Redis and clears the session cookie.
 */
export const serverSignOut = createServerFn().handler(async () => {
  const sessionCookie = getCookie(SESSION_COOKIE_NAME);
  // @ts-expect-error string | undefined passed to string parameter
  const sessionId = parseSessionCookie(sessionCookie);

  // Destroy session in Redis if we have a valid session ID
  if (sessionId) {
    try {
      await destroySession(sessionId);
    } catch {
      // Log already handled inside destroySession — continue to clear cookie
    }
  }

  // Clear the session cookie regardless
  const clearCookie = buildClearSessionCookie();
  setCookie(clearCookie.name, clearCookie.value, clearCookie.options);
});
